import { Box } from '@primer/react';
import styled from 'styled-components';
import { ModelEntityType } from '@upright/common/model/input/types';
import { Link } from '@upright/ui/common/generic/components/primer-custom/link';
import ColumnsIcon from '@upright/ui/icons/img/manual/column-chart-mid-max-min.svg';
import { showGroupfolioHistogramBlock } from '@upright/ui/views/model-entity-views/groupfolio-view/histogram-block';
import { PropsWithChildren } from 'react';
import { useNetImpactLensContext } from '../../../common/use-net-impact-lens-context';
import { formatPercentileIntoTopPercent } from '../../../common/utils';
import { themeGet } from '@upright/ui/common/generic/theme/custom-theme';

export const ImpactRankInfo = (props: { entityType: ModelEntityType | undefined }) => {
  if (!props.entityType) {
    return <></>;
  }
  const isGroup = [ModelEntityType.GROUP, ModelEntityType.PORTFOLIO].includes(props.entityType);
  const isCompany = props.entityType === ModelEntityType.COMPANY;
  const feature = 'nir'; // always nir, as that is what histogram below shows

  if (isGroup) {
    return <GroupPercentile feature={feature} />;
  }

  if (isCompany) {
    return <CompanyPercentile feature={feature} />;
  }
  return <></>;
};

const GroupPercentile = (props: { feature: 'nir' | 'nis' }) => {
  const context = useNetImpactLensContext();
  const groupType = context?.data?.groupType;
  const showHistogram = showGroupfolioHistogramBlock(groupType);

  if (!context) {
    return <></>;
  }

  const data = context.data;
  const percentileMap = data?.topImpactData?.distinctnessData;
  const percentile = percentileMap?.get(props.feature)?.percentile;

  if (!showHistogram) {
    return <></>;
  }
  if (!percentile) {
    return <NoRankAvailable />;
  }

  return (
    <CommonLayout>
      <Box as="span">
        Ranks in the
        <Box as="span" fontWeight="bold">
          {' '}
          {formatPercentile(percentile)}{' '}
        </Box>
        <DistributionTextAndLink entityType="group" />
      </Box>
    </CommonLayout>
  );
};

const CompanyPercentile = (props: { feature: 'nir' | 'nis' }) => {
  const context = useNetImpactLensContext();

  if (!context) {
    return <></>;
  }

  const data = context.data;
  const percentileMap = data?.topImpactData?.distinctnessData;
  const percentile = percentileMap?.get(props.feature)?.percentile;

  if (!percentile) {
    return <NoRankAvailable />;
  }
  return (
    <CommonLayout>
      <Box as="span">
        Ranks in the
        <Box as="span" fontWeight="bold">
          {' '}
          {formatPercentile(percentile)}{' '}
        </Box>
        <DistributionTextAndLink entityType="company" />
      </Box>
    </CommonLayout>
  );
};

const CommonLayout = (props: PropsWithChildren) => (
  <Box sx={{ display: 'flex', alignItems: 'start', gap: 2, width: '50%' }}>
    <Box pt={'2px'}>
      <ColumnsIconRoot />
    </Box>
    <Box as="span" sx={{ width: '100%' }}>
      {props.children}
    </Box>
  </Box>
);

const NoRankAvailable = () => (
  <CommonLayout>
    <Box>No ranking information available</Box>
  </CommonLayout>
);

/**
 * The link points at the histogram this percentile comes from, so the two must name the same
 * population. For companies that is the ACWI IMI benchmark universe, not everything Upright
 * models (PLT-6236); funds are ranked against the Upright fund database.
 */
const DistributionTextAndLink = (props: { entityType: 'group' | 'company' }) => (
  <Box as="span">
    across the{' '}
    <Link indicator="underline" to="#impact-profile-histogram-title">
      {props.entityType === 'group' ? 'global universe of funds' : 'benchmark company universe'}
    </Link>
  </Box>
);

function formatPercentile(percentile: number) {
  const topPercent = formatPercentileIntoTopPercent(percentile);
  return `top ${topPercent}`;
}

const ColumnsIconRoot = styled(ColumnsIcon)`
  width: 14px;
  height: 14px;
  color: ${themeGet('colors.icon.dark')};
`;
